import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
const BASE_COLOR = '#e2374b';
const FIELD_COLOR = '#1565C0';
const EASE = [0.23, 1, 0.32, 1] as const;
const HOME_CENTER: [number, number] = [32, -92];
const HOME_ZOOM = 4;
const FIELD_ZOOM = 11;
type View = {
  mode: 'home';
} | {
  mode: 'route';
  org: string;
} | {
  mode: 'field';
  org: string;
  site: number;
};
function pin(color: string, size = 14) {
  return L.divIcon({
    className: '',
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid #ffffff;box-shadow:0 0 0 5px ${color}26,0 1px 5px rgba(11,31,63,0.4);"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
const baseIcon = pin(BASE_COLOR);
const fieldIcon = pin(FIELD_COLOR);

/** Samples a gentle arc between two points so the connection reads as a route. */
function arcPoints(from: [number, number], to: [number, number], steps = 48): [number, number][] {
  const midLat = (from[0] + to[0]) / 2;
  const midLng = (from[1] + to[1]) / 2;
  const dLat = to[0] - from[0];
  const dLng = to[1] - from[1];
  const dist = Math.hypot(dLat, dLng) || 1;
  // Bow the curve perpendicular to the straight line.
  const lift = dist * 0.16;
  const cLat = midLat + dLng / dist * lift;
  const cLng = midLng - dLat / dist * lift;
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const u = 1 - t;
    pts.push([u * u * from[0] + 2 * u * t * cLat + t * t * to[0], u * u * from[1] + 2 * u * t * cLng + t * t * to[1]]);
  }
  return pts;
}

/** Drives the map camera from the current view state. */
function Camera({
  view
}: {
  view: View;
}) {
  const map = useMap();
  React.useEffect(() => {
    if (view.mode === 'home') {
      map.flyTo(HOME_CENTER, HOME_ZOOM, {
        duration: 1.1
      });
      return;
    }
    const org = SISTER_ORGANIZATIONS.find(o => o.name === view.org);
    if (!org) return;
    const sites = org.partnerSites ?? [];
    if (view.mode === 'field' && sites[view.site]) {
      map.flyTo(sites[view.site].coords, FIELD_ZOOM, {
        duration: 2
      });
      return;
    }
    if (sites.length > 0) {
      map.flyToBounds(L.latLngBounds([org.coords, ...sites.map(s => s.coords)]), {
        padding: [70, 70],
        duration: 1.3
      });
    } else {
      map.flyTo(org.coords, 7, {
        duration: 1.2
      });
    }
  }, [view, map]);
  return null;
}
interface NetworkMapProps {
  isEs: boolean;
}
export function NetworkMap({
  isEs
}: NetworkMapProps) {
  const [view, setView] = useState<View>({
    mode: 'home'
  });
  /** On small screens the card can be folded down so it stops covering the map. */
  const [collapsed, setCollapsed] = useState(false);
  const selectedName = view.mode === 'home' ? null : view.org;
  const activeOrg = SISTER_ORGANIZATIONS.find(o => o.name === selectedName);
  const activeSites = activeOrg?.partnerSites ?? [];
  return <div className="relative w-full h-full" data-id="element-3027" data-mp="3kw5p2fmsjyes">
      <MapContainer center={HOME_CENTER} zoom={HOME_ZOOM} minZoom={3} scrollWheelZoom zoomControl className="w-full h-full bg-[#eef2f7]" data-id="element-3028" data-mp="3ov4nrihxiyxs">
        <TileLayer attribution="Tiles &copy; Esri" url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" maxZoom={16} data-id="element-3029" data-mp="2x50a02ooda5j" />
        <TileLayer attribution="" url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}" maxZoom={16} data-id="element-3030" data-mp="3643tv8vo1tl3" />

        <Camera view={view} data-id="element-3031" data-mp="2dfpuaooxip7f" />

        {SISTER_ORGANIZATIONS.map(org => <React.Fragment key={org.name} data-id="element-3032" data-mp="2iesplhf3xh27">
            {selectedName === org.name ? (org.partnerSites ?? []).map(site => <Polyline key={`arc-${site.name}`} positions={arcPoints(org.coords, site.coords)} pathOptions={{
          color: FIELD_COLOR,
          weight: 2.5,
          opacity: 0.85,
          dashArray: '7 9'
        }} data-id="element-3033" data-mp="ox3h4sp6lmhl" />) : null}

            <Marker position={org.coords} icon={baseIcon} eventHandlers={{
          click: () => setView(cur => cur.mode !== 'home' && cur.org === org.name ? {
            mode: 'home'
          } : {
            mode: 'route',
            org: org.name
          })
        }} data-id="element-3034" data-mp="17pft875n024x" />

            {selectedName === org.name ? (org.partnerSites ?? []).map((site, i) => <Marker key={`site-${site.name}`} position={site.coords} icon={fieldIcon} eventHandlers={{
          click: () => setView({
            mode: 'field',
            org: org.name,
            site: i
          })
        }} data-id="element-3035" data-mp="3efvorbmu8dpf" data-mpi={i} />) : null}
          </React.Fragment>)}
      </MapContainer>

      {activeOrg ? <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="absolute left-3 bottom-3 right-3 sm:left-4 sm:bottom-4 sm:right-auto sm:max-w-xs z-[1000] rounded-xl bg-white px-4 py-3.5 sm:p-5 shadow-xl" data-id="element-3036" data-mp="2kktx7qmes86y">
          <div className="flex items-start justify-between gap-3" data-id="element-3037" data-mp="2gkjm5zjrerl8">
            <div className="min-w-0" data-id="element-3038" data-mp="9uvd9ge6h3ev">
              <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.2em] text-[#1565C0] mb-1" data-id="element-3039" data-mp="9rqy5nz6cn9f">
                {isEs ? activeOrg.focusEs : activeOrg.focus}
              </p>
              <h4 className="font-bebas text-xl tracking-[0.04em] text-[#0b1f3f] leading-tight" data-id="element-3040" data-mp="cfkv8tpbmy46">
                {activeOrg.name}
              </h4>
            </div>
            <button type="button" onClick={() => setCollapsed(c => !c)} aria-expanded={!collapsed} className="sm:hidden flex-shrink-0 -mt-0.5 -mr-1 px-2 py-1 font-nunito text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400" data-id="element-3041" data-mp="3emq2835ckk0">
              {collapsed ? isEs ? 'Más' : 'More' : isEs ? 'Ocultar' : 'Hide'}
            </button>
          </div>

          <p className={`font-nunito text-xs text-gray-500 mt-1.5 ${collapsed ? 'hidden sm:block' : ''}`} data-id="element-3042" data-mp="hd2yc3gnqj49">
            {activeOrg.location}
            {activeSites.length > 0 ? ` → ${activeSites.length} ${isEs ? activeSites.length === 1 ? 'sitio en Honduras' : 'sitios en Honduras' : activeSites.length === 1 ? 'site in Honduras' : 'sites in Honduras'}` : ''}
          </p>
          <p className={`font-nunito text-xs text-gray-700 leading-relaxed mt-2 mb-3 ${collapsed ? 'hidden sm:block' : ''}`} data-id="element-3043" data-mp="1wc0ht7uyvdp6">
            {isEs ? activeOrg.descriptionEs : activeOrg.description}
          </p>

          {activeSites.length > 0 ? <div className="mt-3 mb-3 flex flex-col gap-2" data-id="element-3044" data-mp="2a1kepx1v9gqk">
              <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400" data-id="element-3045" data-mp="2a46nu14eo4aw">
                {isEs ? 'Su trabajo en Honduras' : 'Their work in Honduras'}
              </p>
              {activeSites.map((site, i) => {
          const isOpen = view.mode === 'field' && view.site === i;
          return <button key={site.name} type="button" onClick={() => setView(isOpen ? {
            mode: 'route',
            org: activeOrg.name
          } : {
            mode: 'field',
            org: activeOrg.name,
            site: i
          })} className="w-full text-left rounded-lg px-3.5 py-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-colors" style={{
            backgroundColor: isOpen ? '#0b1f3f' : '#1565C0'
          }} data-id="element-3046" data-mp="39bijgt4ko7yr" data-mpi={i}>
                    {isOpen ? isEs ? '← Ver todos los sitios' : '← Back to all sites' : `${isEs ? 'Acercar a' : 'Zoom into'} ${site.location.split(',')[0]}`}
                  </button>;
        })}
            </div> : null}

          <div className="flex items-center gap-4" data-id="element-3047" data-mp="ide6ykgzgml0">
            {activeOrg.url ? <a href={activeOrg.url} target="_blank" rel="noopener noreferrer" className="font-nunito text-[11px] font-bold uppercase tracking-[0.14em] text-[#cf142b] hover:opacity-70 transition-opacity" data-id="element-3048" data-mp="n5p3regffs9c">
                {isEs ? 'Sitio web →' : 'Website →'}
              </a> : null}
            <button type="button" onClick={() => setView({
          mode: 'home'
        })} className="font-nunito text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400 hover:text-[#0b1f3f] transition-colors" data-id="element-3049" data-mp="1641f96ut9e83">
              {isEs ? 'Cerrar' : 'Close'}
            </button>
          </div>
        </motion.div> : <p className="absolute left-5 bottom-5 z-[1000] font-nunito text-[#0b1f3f]/50 text-[10px] font-bold uppercase tracking-[0.2em] pointer-events-none" data-id="element-3050" data-mp="3ct6cgr1c5a8v">
          {isEs ? 'Seleccione un punto rojo' : 'Select a red point'}
        </p>}
    </div>;
}